import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { DataTablesModule, DataTableDirective } from 'angular-datatables';
import { Router, RouterLink } from '@angular/router';
import { Helpers } from '../../../helpers/helpers';
import { ToastrService } from 'ngx-toastr';
import { ChatbotClientService } from '../../../services/chatbot-client.service';
import Swal from 'sweetalert2';
import { Status } from '../../../enums/status';
import { LanguageApp } from '../../../config/datatable-config';

@Component({
  standalone: true,
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss'],
  imports: [
    DataTablesModule,
    RouterLink
  ]
})

export class ClientComponent implements AfterViewInit, OnDestroy, OnInit {

  @ViewChild(DataTableDirective, { static: false })
  dtElement: DataTableDirective;
  dtOptions: any = {};
  dtTrigger: Subject<any> = new Subject();
  listenerFn!: () => void;

  constructor(private helper: Helpers,
    private router: Router,
    private service: ChatbotClientService,
    private renderer: Renderer2,
    private toastr: ToastrService) {
  }

  ngOnInit() {
    this.setDatatable();
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
    this.listenerFn = this.renderer.listen('document', 'click', (event) => {
      if (event.target.hasAttribute("change-status-client-id")) {
        const id = event.target.getAttribute("change-status-client-id");
        const status = event.target.checked ? Status.Activo : Status.NoActivo;
        this.changeStatus(id, status);
      } else if (event.target.hasAttribute("delete-client-id")) {
        this.delete(event.target.getAttribute("delete-client-id"));
      } else if (event.target.hasAttribute("edit-client-id")) {
        this.router.navigate(["/admin/client/edit/" + event.target.getAttribute("edit-client-id")]);
      } else if (event.target.hasAttribute("view-projects-id")) {
        this.router.navigate(["/admin/project/" + event.target.getAttribute("view-projects-id")]);
      }
    });
    this.helper.setDataTableSearchDelay();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
    if (this.listenerFn) {
      this.listenerFn();
    }
  }

  setDatatable() {
    const that = this;
    this.dtOptions = {
      responsive: true,
      paging: true,
      pageLength: 10,
      ordering: false,
      lengthChange: true,
      searching: true,
      info: true,
      serverSide: true,
      processing: true,
      language: LanguageApp.spanish_datatables,
      dom: LanguageApp.custom_dom_datatables,
      ajax: (dataTablesParameters: any, callback) => {
        that.service.getDataTable(dataTablesParameters).subscribe(resp => {
          callback({
            recordsTotal: resp.recordsTotal,
            recordsFiltered: resp.recordsFiltered,
            data: resp.data
          });
        }, (err) => {
          Swal.fire({
            icon: 'error',
            buttonsStyling: false,
            customClass: {
              cancelButton: 'btn-outline-black',
              confirmButton: 'btn-red'
            },
            title: 'Error al recuperar los datos',
            text: 'Ocurrió un error'
          });

        });
      },
      columns: [
        { data: "CreationDate", className: "text-center" },
        { data: "ClientName" },
        { data: "Status" },
        { data: "IdClient" }
      ],
      columnDefs: [
        {
          targets: [2],
          data: "Status",
          className: "align-middle text-center",
          render: function (data, type, full) {
            var retorno = "";
            retorno = '<div class="form-check form-switch">';
            if (data) {
              retorno += '<input type="checkbox" class="form-check-input" change-status-client-id ="' + full.IdClient + '" id="toggle' + full.IdClient + '" checked>';
            }
            else {
              retorno += '<input type="checkbox" class="form-check-input" change-status-client-id ="' + full.IdClient + '" id="toggle' + full.IdClient + '">';
            }
            retorno += '</div>';
            return retorno;
          }
        },
        {
          targets: [3],
          data: "IdClient",
          className: "align-middle text-center text-nowrap",
          render: function (data, type, full) {
            var retorno = '';
            retorno += '<div class="cont__btn-option">';
            retorno += '<button view-projects-id="' + data + '" class="btn-option btn-option-projects" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="top" title="Ver proyectos"><span class="visually-hidden">ver proyectos</span></button>';
            retorno += '<button edit-client-id="' + data + '" class="btn-option btn-option-edit" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="top" title="Editar"><span class="visually-hidden">editar</span></button>';
            retorno += '<button delete-client-id="' + data + '"class="btn-option btn-option-delete" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="top" title="Eliminar"><span class="visually-hidden">Eliminar</span></button>';
            retorno += '</div>';
            return retorno;
          }
        }
      ]
    };
  }

  rerender(): void {
    this.dtElement.dtInstance.then((dtInstance) => {
      dtInstance.draw();
    });
  }

  changeStatus(id: string, status: number) {
    this.service.changeStatus(id, status).subscribe(() => { }, (err) => {
      Swal.fire({
        icon: 'error',
        buttonsStyling: false,
        customClass: {
          cancelButton: 'btn-outline-black',
          confirmButton: 'btn-red'
        },
        title: 'Error al actualizar los datos',
        text: 'Ocurrió un error'
      });
    });
  }

  delete(id: string) {
    Swal.fire({
      title: '¿Quiere eliminar este elemento?',
      text: "El elemento desaparecerá del administrador",
      icon: 'warning',
      buttonsStyling: false,
      customClass: {
        cancelButton: 'btn-outline-black',
        confirmButton: 'btn-red'
      },
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Eliminar <i class="fa-solid fa-trash-can"></i>',
      cancelButtonText: 'Cancelar <i class="fa-solid fa-xmark"></i>',
      reverseButtons: true,
      focusCancel: true
    }).then((result) => {
      if (result.value) {
        this.service.changeStatus(id, Status.Baja).subscribe(() => {
          this.toastr.success("El elemento se eliminó correctamente.");
          this.rerender();
        }, (err) => {
          Swal.fire({
            icon: 'error',
            buttonsStyling: false,
            customClass: {
              cancelButton: 'btn-outline-black',
              confirmButton: 'btn-red'
            },
            title: 'Error al eliminar el elemento',
            text: 'Ocurrió un error'
          });
        });
      }
    })
  }

}

